import { CalculateSalesTaxEvent, trackEvent } from '@otter-ai/telemetry';
import { useCheckout } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { shippingAddressIsUpdatingAtom } from '#atoms';

export function useUpdateShippingAddress({
  setIsLoading,
}: {
  setIsLoading?: (isLoading: boolean) => void;
}) {
  const { updateShippingAddress, tax, total, minorUnitsAmountDivisor } =
    useCheckout();
  const setShippingAddressIsUpdating = useSetAtom(
    shippingAddressIsUpdatingAtom,
  );
  const lastAddressRef = useRef<{
    country: string;
    postal_code: string;
  } | null>(null);

  const mutation = useMutation({
    mutationFn: async (address: {
      country: string;
      postal_code: string;
      name?: string;
    }) => {
      lastAddressRef.current = address;
      await updateShippingAddress({
        address: {
          country: address.country,
          postal_code: address.postal_code,
          // Stripe workaround
          line1: '',
        },
        name: address.name,
      });
    },
    onMutate: () => {
      setShippingAddressIsUpdating(true);
      setIsLoading?.(true);
    },
    onSettled: () => {
      setShippingAddressIsUpdating(false);
      setIsLoading?.(false);
    },
  });

  useEffect(() => {
    if (!lastAddressRef.current || mutation.isPending) return;

    const address = lastAddressRef.current;
    const userCountryCode = address.country;
    const userZipCode = address.postal_code;

    if (
      tax?.status === 'requires_billing_address' ||
      tax?.status === 'requires_shipping_address' ||
      mutation.error
    ) {
      trackEvent(
        CalculateSalesTaxEvent({
          UserTaxCountryCode: userCountryCode,
          UserTaxZipCode: userZipCode,
          Status: 'error',
          ErrorMessage: mutation.error?.message || tax.status,
        }),
      );
    } else if (
      tax?.status === 'ready' &&
      total?.taxExclusive?.minorUnitsAmount !== undefined &&
      total?.total?.minorUnitsAmount !== undefined
    ) {
      trackEvent(
        CalculateSalesTaxEvent({
          UserTaxCountryCode: userCountryCode,
          UserTaxZipCode: userZipCode,
          SalesTaxAmount:
            total.taxExclusive.minorUnitsAmount /
            (minorUnitsAmountDivisor ?? 100),
          PriceDisplayed:
            total.total.minorUnitsAmount / (minorUnitsAmountDivisor ?? 100),
          Status: 'success',
        }),
      );
    }
  }, [
    tax?.status,
    total?.taxExclusive?.minorUnitsAmount,
    total?.total?.minorUnitsAmount,
    mutation.isPending,
    mutation.error,
    minorUnitsAmountDivisor,
  ]);

  return mutation;
}
